import { useEffect, type RefObject } from 'react';
import { isReadOnlyView } from '../boot/view-handoff';

/**
 * Superadmin consultation — freeze the CONTENT of a settings panel without freezing the panel.
 *
 * The store refuses every edit in a read-only tab (see `commit` / `updateThemeSettings`), so nothing a
 * consultation types can reach the document. What was left was the ECHO: a text control keeps its own
 * `local` state, so a typed letter still appeared in the field even though the page never moved, and a
 * colour picker still opened on a value it could not change. Founder, 25/10/2026: « on doit pas pouvoir
 * modifier ».
 *
 * `inert` is set on the panel's CHILDREN, never on the scroll container itself: an inert node is not
 * hit-testable, so marking the scroller would have cost the mouse wheel — and reading the settings is
 * the whole point of a consultation. Children are re-marked when the panel swaps forms (another section
 * selected), hence the observer.
 */
export function useInertWhenReadOnly(
  ref: RefObject<HTMLElement | null>,
  deps: unknown[] = [],
): void {
  useEffect(() => {
    const host = ref.current;
    if (!host || !isReadOnlyView()) return;
    const mark = () => {
      for (const child of Array.from(host.children)) child.setAttribute('inert', '');
    };
    mark();
    const observer = typeof MutationObserver !== 'undefined' ? new MutationObserver(mark) : null;
    observer?.observe(host, { childList: true });
    return () => {
      observer?.disconnect();
      for (const child of Array.from(host.children)) child.removeAttribute('inert');
    };
    // `deps` re-runs this when the panel MOUNTS its body — an inspector with nothing selected renders no
    // body at all, so the first run has no node to mark.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...deps]);
}
